import { useState, useEffect, lazy, Suspense } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

const MarkdownEditor = lazy(() =>
  import('@/components/MarkdownEditor').then((mod) => ({ default: mod.MarkdownEditor }))
);
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';

interface EditPostModalProps {
  post: {
    id: string;
    slug: string;
    title: string;
    details?: string;
    categoryId?: string;
    imageURLs?: string[];
  };
  isOpen: boolean;
  onClose: () => void;
  categories?: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
}

export function EditPostModal({
  post,
  isOpen,
  onClose,
  categories = [],
}: EditPostModalProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(post.title);
  const [details, setDetails] = useState(post.details || '');
  const [categoryId, setCategoryId] = useState(post.categoryId || '');
  const [error, setError] = useState('');

  // Reset form when post changes
  useEffect(() => {
    setTitle(post.title);
    setDetails(post.details || '');
    setCategoryId(post.categoryId || '');
    setError('');
  }, [post]);

  const updatePostMutation = useMutation({
    mutationFn: (data: { title: string; details: string; categoryId?: string }) =>
      api.posts.update(post.slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post', post.slug] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      onClose();
      setError('');
      toast.success('Post updated');
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update post');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    updatePostMutation.mutate({
      title: title.trim(),
      details: details.trim(),
      categoryId: categoryId || undefined,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Edit post</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              maxLength={200}
              required
            />
            <p className="text-xs text-muted-foreground">{title.length}/200 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Details (optional)</Label>
            <Suspense fallback={<div className="h-32 w-full bg-muted animate-pulse rounded-md" />}>
              <MarkdownEditor
                content={details}
                onChange={setDetails}
                placeholder="Provide more context, use cases, or examples..."
              />
            </Suspense>
          </div>

          {categories.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="category">Category (optional)</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updatePostMutation.isPending || !title.trim()}
            >
              {updatePostMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
